import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { $fetch } from "@/lib/fetch";
import { scanLogsApi } from "@/services/participant-scan-logs/participant-scan-logs.api";
import {
  scanLogsDtoSchema,
  type ScanLogResponseDto,
} from "slde-api/dto-schemas";
import { Loader2, Download } from "lucide-react";
import React from "react";

type ScanLogsFilter =
  | { type: "participant"; identifier: string; title: string }
  | { type: "company"; companyId: number; title: string };

interface ScanLogsDialogProps {
  filter: ScanLogsFilter | null;
  onClose: () => void;
}

export function ScanLogsDialog({ filter, onClose }: ScanLogsDialogProps) {
  const params =
    filter?.type === "participant"
      ? { identifier: filter.identifier }
      : filter?.type === "company"
        ? { companyId: filter.companyId }
        : undefined;

  const { data, isLoading } = useQuery({
    // @ts-expect-error - fixme
    queryKey: scanLogsApi.scanLogs.queryKey(params),
    queryFn: () =>
      // @ts-expect-error - fixme
      $fetch(scanLogsApi.scanLogs.route(params), {
        output: scanLogsDtoSchema,
        throw: true,
      }),
    enabled: !!filter,
  });

  const [isExporting, setIsExporting] = React.useState(false);

  async function handleExport() {
    if (!params) return;
    setIsExporting(true);
    try {
      const token = localStorage.getItem("auth-token") ?? "";
      const baseUrl = (process.env.BUN_PUBLIC_API_URL ?? "").replace(/\/$/, "");
      const res = await fetch(
        // @ts-expect-error - fixme
        `${baseUrl}/${scanLogsApi.scanLogs.exportRoute(params)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const csv = await res.text();
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "logi.csv";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <Dialog open={!!filter} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="min-w-4xl">
        <DialogHeader>
          <DialogTitle>Logi skanowania — {filter?.title}</DialogTitle>
        </DialogHeader>

        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            disabled={isExporting || isLoading}
            onClick={handleExport}
          >
            {isExporting ? <Loader2 className="animate-spin" /> : <Download />}
            Eksport CSV
          </Button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin" />
          </div>
        ) : (
          <div className="overflow-auto max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dodano przez</TableHead>
                  <TableHead>Imię i nazwisko</TableHead>
                  <TableHead>PWZ / Legitymacja</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.items.length ? (
                  data.items.map((log: ScanLogResponseDto) => (
                    <TableRow key={log.id}>
                      <TableCell>{log.addedBy ?? "—"}</TableCell>
                      <TableCell>
                        {log.firstName} {log.lastName}
                      </TableCell>
                      <TableCell>{log.identifier}</TableCell>
                      <TableCell>
                        {new Date(log.createdAt).toLocaleString("pl-PL")}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="h-24 text-center text-muted-foreground"
                    >
                      Brak logów
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
